import {AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, HostListener, Injectable, Injector, input, output, signal, Signal, viewChild} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {distinctUntilChanged, Observable, of, switchMap} from "rxjs";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {AgendaelementId, DokumentId, EnkelGruppeModell, MøteId, SaksdokumentModell, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {MatDrawer, MatDrawerContainer, MatDrawerContent, MatDrawerToggleResult} from "@angular/material/sidenav";
import {DelegerteDokumentDetaljerOperasjoner, DelegerteDokumentDetaljerOperasjoner_DI_TOKEN, DokumentDetaljerKomponent} from "../../../dokument/komponenter/dokument-detaljer/dokument-detaljer";
import {SakslisteKomponent} from "../saksliste/saksliste";
import {SakVisKomponent} from "../sak-vis/sak-vis";
import {PauseVisKomponent} from "../pause-vis/pause-vis";
import {AgendaelementModellDLM, SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {NyeSakerSkjemaKontroller} from "../nye-saker-skjema/nye-saker-skjema";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";

import {ValgtDokumentRegister} from "../../tjenester/ValgtDokumentRegister";
import {SaksdokumentDatatjeneste} from "felles-ng2/tjenester/datalager/SaksdokumentDatatjeneste";
import {ValgtSakRegister} from "web-ng2/tjenester/ValgtSakRegister";
import {DelegerteDokumentMenyOperasjoner, DelegerteDokumentMenyOperasjoner_DI_TOKEN} from "web-ng2/moduler/dokument/komponenter/dokument-meny/dokument-meny";
import {DelegerteDokumentvisningOperasjoner, DelegerteDokumentvisningOperasjoner_DI_TOKEN} from "web-ng2/moduler/dokument/komponenter/dokumentvisning/DokumentvisningBasisKomponent";
import {MøteModellDLM} from "felles-ng2/tjenester/datalager/MøteDatatjeneste";
import {FilvelgerViaInputFelt} from "felles-ng2/tjenester/filopplasting/FilvelgerViaInputFelt";
import {LukkMatDrawerKnappKomponent} from "web-ng2/delt/komponenter/lukk-mat-drawer-knapp/lukk-mat-drawer-knapp";
import {FormaterZonedDateTimePipe} from "felles-ng2/pipes/FormaterZonedDateTimePipe";
import {MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {tomEnkelGruppeModell} from "felles-ng2/modell/tommeModeller";
import {TranslateModule} from "@ngx-translate/core";
import {PresentasjonsmodusTjeneste} from "web-ng2/tjenester/PresentasjonsmodusTjeneste";
import {ventMillis} from "felles-ng2/test/støttefunksjoner/vent";
import {timeoutSomPromise} from "felles-ng2/støttefunksjoner/promiser";
import {ToggleKlasseVedScrollDirektiv} from "web-ng2/delt/direktiver/toggleKlasseVedScroll/toggleKlasseVedScroll";
import {CdkScrollable} from "@angular/cdk/scrolling";

const debug = initDebugger('SakListeOgDetaljerKomponent');

/**
 * Viser saksliste med støtte for å klikke på en sak og se saksdetaljer i en to-delt layout
 */
@Component({
    selector: 'sak-liste-og-detaljer',
    templateUrl: 'sak-liste-og-detaljer.html',
    styleUrl: 'sak-liste-og-detaljer.scss',
    host: {
        '[class.splittvisning]': 'viserSaksdetaljer()',
        //markørklasse som får basis.scss til å låse scrollen på utsiden slik at saksliste og saksdetaljer kan scrolle hver for seg
        '[class.basisside-uiview__fast-hoeyde]': 'viserSaksdetaljer()',
        '[class.viserDokumentdetaljer]': 'viserDokumentdetaljer()',
        '[class.presentasjonsmodus]': 'presentasjonsmodusAktivert()'
    },
    imports: [
        DokumentDetaljerKomponent,
        SakslisteKomponent,
        SakVisKomponent,
        PauseVisKomponent,
        NyeSakerSkjemaKontroller,
        MatDrawerContainer,
        MatDrawer,
        MatDrawerContent,
        LukkMatDrawerKnappKomponent,
        FormaterZonedDateTimePipe,
        MatIconButton,
        MatIcon,
        TranslateModule,
        ToggleKlasseVedScrollDirektiv,
        CdkScrollable
    ],
    providers: [
        ValgtDokumentRegister,
        {provide: DelegerteDokumentDetaljerOperasjoner_DI_TOKEN, useFactory: (komponent: SakListeOgDetaljerKomponent) => komponent.delegerteDokumentDetaljerOperasjoner(), deps: [SakListeOgDetaljerKomponent]},
        {provide: DelegerteDokumentMenyOperasjoner_DI_TOKEN, useFactory: (komponent: SakListeOgDetaljerKomponent) => komponent.delegerteDokumentMenyOperasjoner(), deps: [SakListeOgDetaljerKomponent]},
        {provide: DelegerteDokumentvisningOperasjoner_DI_TOKEN, useFactory: (komponent: SakListeOgDetaljerKomponent) => komponent.delegerteDokumentvisningOperasjoner(), deps: [SakListeOgDetaljerKomponent]}
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
class SakListeOgDetaljerKomponent implements AfterViewInit {
    static ajsSelector = 'sakListeOgDetaljer';

    private matDrawerContainer = viewChild.required(MatDrawerContainer);
    private sakVisKomponent = viewChild(SakVisKomponent);
    private sakslisteKomponent = viewChild.required(SakslisteKomponent);
    private dokumentDetaljerKomponent = viewChild(DokumentDetaljerKomponent);
    /** Eget scrollområde for saksdetaljer (finnes kun når en sak er valgt), se .saksdetaljer-scrollomraade i html/scss */
    private saksdetaljerScrollomraade = viewChild<ElementRef<HTMLElement>>('saksdetaljerScrollomraade');

    /** Så lenge denne komponenten brukes i ajs og kaller har behov for å aksessere komponent-instansen kan kaller lytte til denne eventen */
    komponentKlar = output<SakListeOgDetaljerKomponent>();

    moete = input.required<MøteModellDLM>();

    saker: Signal<AgendaelementModellDLM[]>;
    sorteringAktivert = signal(false);
    nyeSakerSkjemaDeaktivert = computed(() => this.erNyeSakerSkjemaDeaktivert());
    aktivSakId = this.valgtSakRegister.valgt;
    aktivSak = computed(() => this.saker().find(sak => sak.id === this.aktivSakId()) ?? null);
    viserSaksdetaljer = computed(() => erIkkeNil(this.aktivSakId()));
    /** Sann når saken bruker har valgt er en pause - da vises <pause-vis> i stedet for <sak-vis> */
    aktivSakErPause = computed(() => this.aktivSak()?.erPause ?? false);
    sakslisteBreddeParam = signal('275px');
    /** Dokumentet valgt av bruker, settes når valgt dokument settes i tjenesten {@link ValgtDokumentRegister}.
     * Når satt til ikke-null vil også dokumentdetaljer vises (via {@link #viserDokumentdetaljer}) */
    valgtDokument: Signal<SaksdokumentModell | null>;
    /** Styrer hvorvidt dokument detaljer vises */
    viserDokumentdetaljer = computed(() => erIkkeNil(this.valgtDokument()));
    kanRedigereDokument = computed(() => this.møteKanRedigeresAvBruker());
    gruppe = computed(() => this.finnGruppe());
    presentasjonsmodusAktivert = computed(() => this.presentasjonsmodusTjeneste.erAktivert());

    constructor(private avhengigheter: SakListeOgDetaljerKomponentAvhengigheter,
                private valgtSakRegister: ValgtSakRegister,
                private valgtDokumentRegister: ValgtDokumentRegister,
                private presentasjonsmodusTjeneste: PresentasjonsmodusTjeneste,
                private filvelger: FilvelgerViaInputFelt,
                private navigasjon: Navigasjon,
                private kontekst: BrukerkontekstTilbyder,
                private injector: Injector) {
        this.oppdaterSakslisteBredde();
        this.saker = toSignal(toObservable(this.moete).pipe(switchMap(møte => this.avhengigheter.hentSaker(møte.id))), {initialValue: []});
        this.valgtDokument = toSignal(toObservable(this.valgtDokumentRegister.valgt).pipe(
            switchMap(dokumentId => erIkkeNil(dokumentId) ? this.avhengigheter.hentSaksdokument(dokumentId) : of(null))),
          {initialValue: null});

        //sørger for å lukke dokumentdetaljer-visningen i høyre marg når aktiv sak endres
        toObservable(this.aktivSakId)
          .pipe(distinctUntilChanged(), takeUntilDestroyed())
          .subscribe(sakId => this.valgtDokumentRegister.nullstillValgt());

        //viser valgt sak fra toppen når bruker velger en annen sak. Saksliste har eget scrollområde og beholder dermed sin scrollposisjon.
        //nullstillingen skjer før ny sak er lastet, slik at scrolling til en forespurt entitet (dyp lenke) i sak-vis fortsatt virker
        effect(() => {
            this.aktivSakId();
            const scrollomraade = this.saksdetaljerScrollomraade()?.nativeElement;
            if (scrollomraade) scrollomraade.scrollTop = 0;
        });
    }

    //eksponert for bruk i ajs
    aktiverSortering(){
        this.sakslisteKomponent().aktiverSortering();
    }

    vedSorteringAktivert(aktivert: boolean) {
        this.sorteringAktivert.set(aktivert);
        if (aktivert) this.navigasjon.gåTilMøte(this.moete().id);
    }

    vedVisFulltNyeSakerSkjema(viser: boolean) {
        //i tilfelle skjema ble aktivert mens bruker var på sak-detaljer siden
        //sørger vi for å vise sakslisten i full bredde ved å nav til møte
        if (viser) this.navigasjon.gåTilMøte(this.moete().id);
    }

    vedNyeSakerOpprettet(nyeSakerId: AgendaelementId[]) {
        //kaller Async-versjonen fordi NavigasjonDeaktivering.reaktiverNavigasjon gjør kall mot ajs pakket inn i ajsRootScope.$evalAsync(..)
        //dermed er navigasjon fortsatt forhindret når vedNyeSakerOpprettet kalles
        this.navigasjon.gåTilMøteOgSakAsync(this.moete().id, nyeSakerId[0]);
    }

    skjulDokumentDetaljer() {
        this.valgtDokumentRegister.nullstillValgt();
    }

    gaaTilMoete() {
        this.navigasjon.gåTilMøte(this.moete().id);
    }

    /** DelegerteDokumentDetaljerOperasjoner implementasjon - legges til DI-kontekst via @Component */
    delegerteDokumentDetaljerOperasjoner(): DelegerteDokumentDetaljerOperasjoner {
        return {
            registrerNyDokumentversjon: saksdokument => this.registrerNyDokumentversjon(saksdokument as SaksdokumentModell),

            åpneDokumentInline: saksdokument => this.åpneDokumentInline(saksdokument as SaksdokumentModell)
        }
    }

    /** DelegerteDokumentMenyOperasjoner implementasjon - legges til DI-kontekst via @Component.
     * Merk at vi forutsetter at dokumentDetaljer vises når disse kallene skjer, ved klikk på dokument-meny vil klikket på menyen ha ført til at
     * this.visDetaljerFor(..) allerede har blitt kalt, og følgelig vises dokumentDetaljer */
    delegerteDokumentMenyOperasjoner(): DelegerteDokumentMenyOperasjoner {
        return {
            visAlleVersjoner: async dokumentversjon => {
                await this.visDetaljerFor(dokumentversjon as SaksdokumentModell);
                this.dokumentDetaljerKomponent()!.fokuserVersjonerBlokk();
            },

            visSignatarer: async dokumentversjon => this.visSignatarerForDokument(dokumentversjon as SaksdokumentModell),

            visSakerArkivdokumentBehandlesI: async dokumentversjon => {
                await this.visDetaljerFor(dokumentversjon as SaksdokumentModell);
                this.dokumentDetaljerKomponent()!.fokuserSakerArkivdokumentBehandlesIBlokk();
            },

            visSkjerming: dokumentversjon => this.visSkjermingForDokument(dokumentversjon as SaksdokumentModell),

            registrerNyDokumentversjon: dokumentversjon => this.registrerNyDokumentversjon(dokumentversjon as SaksdokumentModell),

            tilgjengeligFunksjonalitet: dokumentversjon => {
                return {
                    rediger: this.møteKanRedigeresAvBruker(),
                    behandlesISaker: false, // kun aktuell i arkiv
                    // alle øvrige funksjoner skal alltid være aktive i saksdokumentlisting
                    favorittmarkering: true,
                    signering: true,
                    versjonsvisning: true,
                    skjerming: true
                };
            }
        };
    }

    /** DelegerteDokumentvisningOperasjoner implementasjon - legges til DI-kontekst via @Component */
    delegerteDokumentvisningOperasjoner(): DelegerteDokumentvisningOperasjoner {
        return {
            visSkjerming: dokumentversjon => this.visSkjermingForDokument(dokumentversjon as SaksdokumentModell),
            visSignatarer: dokumentversjon => this.visSignatarerForDokument(dokumentversjon as SaksdokumentModell)
        };
    }

    @HostListener('window:resize')
    onResize() {
        this.oppdaterSakslisteBredde();
    }

    ngAfterViewInit(): void {
        //vis/skjul dokumentdetaljer-panelet når viserDokumentdetaljer endres
        //denne effect kan ikke kalles før matDrawerContainer er initialisert - derfor i ngAfterViewInit
        effect(() => {
            if (this.viserDokumentdetaljer()) this.visDokumentDetaljerPanel();
            else this.skjulDokumentDetaljerPanel();
        }, {injector: this.injector});

        this.komponentKlar.emit(this);
    }

    //private hjelpemetoder

    private finnGruppe(): EnkelGruppeModell {
        return this.kontekst.autentisertBrukerSomSignal()?.finnAutentiserteGruppe(this.moete().eiendeGruppeId) ?? tomEnkelGruppeModell();
    }

    private oppdaterSakslisteBredde() {
        const bredde = window.innerWidth <= 1190 ? '75px' : '275px';
        this.sakslisteBreddeParam.set(bredde);
    }

    /** Sann dersom møtet ikke er låst og bruker har skriverettigheter i gruppen */
    private møteKanRedigeresAvBruker() {
        return erIkkeNil(this.moete())
          && this.kontekst.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, this.moete().eiendeGruppeId)
          && !this.moete().laastForRedigering;
    }

    /** Forhindrer at ny-sak inputfeltet vises når saksrekkefølge er redigert eller dersom bruker ikke har tilgang eller dersom presentasjonsmodus er aktivert */
    private erNyeSakerSkjemaDeaktivert() {
        return this.sorteringAktivert() || !this.møteKanRedigeresAvBruker() || this.presentasjonsmodusAktivert();
    }

    /** Viser detaljer for angitt dokument i høyre-del av vindu  */
    private async visDetaljerFor(dokument: SaksdokumentModell) {
        debug(`visDetaljerFor ${dokument.filnavn}`)
        //fører til at this.valgtDokument blir oppdatert (dvs blir hentet fra SaksdokumentDatatjeneste som observerbar fra lokallager - dvs fører ikke til http-request)
        //som igjen fører til at viserDokumentdetaljer blir satt til true
        this.valgtDokumentRegister.setValgt(dokument.dokumentId);
        //venter her slik at DokumentDetaljerKomponent har rukket å bli tegnet ut
        await timeoutSomPromise(100);
    }

    private async visSkjermingForDokument(saksdokument: SaksdokumentModell) {
        await this.visDetaljerFor(saksdokument);
        this.dokumentDetaljerKomponent()!.fokuserSkjermingBlokk();
    }

    private async visSignatarerForDokument(saksdokument: SaksdokumentModell) {
        await this.visDetaljerFor(saksdokument);
        this.dokumentDetaljerKomponent()!.fokuserSigneringBlokk();
    }

    /** Viser detaljer for angitt dokument i høyre-del av vindu  */
    private async visDokumentDetaljerPanel(): Promise<void> {
        if (!this.matDrawerContainer().end!.opened)
            await this.matDrawerContainer().end!.open();
    }

    /** Skjuler detaljer for angitt dokument i høyre-del av vindu  */
    private skjulDokumentDetaljerPanel(): Promise<MatDrawerToggleResult> {
        return this.matDrawerContainer().end!.close();
    }

    /** Lar bruker velge en fil og laster denne opp som ny versjon under angitt saksdokument, delegerer opplastingen til {@link SakVisKomponent} */
    private async registrerNyDokumentversjon(saksdokument: SaksdokumentModell) {
        const fil = await this.filvelger.presenterFilvelgerEnkeltfilStandardformat();
        if (erNil(fil)) return;
        await this.sakVisKomponent()!.registrerNyDokumentversjon(saksdokument, fil);
    }

    /** Åpner inline visning av angitt dokument, delegerer til {@link SakVisKomponent} som så delegerer videre til {@link SakListeDokumenterKomponent#åpneDokumentInline} */
    private åpneDokumentInline(saksdokument: SaksdokumentModell): boolean {
        debug(`åpneDokumentInline ${saksdokument.filnavn}`);
        const dokumentÅpnet = this.sakVisKomponent()!.åpneDokumentInline(saksdokument);
        if (!dokumentÅpnet) console.warn(`SakListeDokumenterKomponent.åpneDokumentInline, fant ikke saksdokument med id ${saksdokument.dokumentId}`);
        else this.skjulDokumentDetaljer();
        return dokumentÅpnet;
    }

}

export default SakListeOgDetaljerKomponent

@Injectable()
export class SakListeOgDetaljerKomponentAvhengigheter {

    constructor(private sakDatatjeneste: SakDatatjeneste, private saksdokumentDatatjeneste: SaksdokumentDatatjeneste) {
    }

    hentSaker(møteId: MøteId): Observable<AgendaelementModellDLM[]> {
        return this.sakDatatjeneste.finnTilknyttetMøteFraLokallager(møteId);
    }

    hentSaksdokument(saksdokumentId: DokumentId): Observable<SaksdokumentModell> {
        return this.saksdokumentDatatjeneste.hentFraFraLokallagerSomObservable(saksdokumentId)
    }

}
